/**
 * 인증 상태 관리 스토어
 * - 로그인/로그아웃 상태
 * - 사용자 정보 관리
 * - 토큰 관리
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 사용자 정보 타입
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
}

/**
 * 인증 상태 타입
 */
interface AuthState {
  // 상태
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;

  // 액션
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  updateUser: (userData: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

/**
 * 인증 스토어
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,

      // 로그인
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          // TODO: 실제 API 호출로 대체
          // 임시 로그인 로직
          await new Promise(resolve => setTimeout(resolve, 1000));

          if (email === 'admin@mungtoday.com' && password === 'admin123') {
            const user: User = {
              id: '1',
              email,
              name: '관리자',
              role: 'admin',
            };

            set({
              isAuthenticated: true,
              user,
              isLoading: false,
              error: null,
            });
          } else {
            throw new Error('이메일 또는 비밀번호가 올바르지 않습니다.');
          }
        } catch (error) {
          set({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error:
              error instanceof Error ? error.message : '로그인에 실패했습니다.',
          });
        }
      },

      // 로그아웃
      logout: () => {
        // 토큰 제거
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth-token');
          localStorage.removeItem('refresh-token');
        }
        
        set({
          isAuthenticated: false,
          user: null,
          error: null,
        });
      },

      // 사용자 정보 설정
      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      // 사용자 정보 업데이트
      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },

      // 로딩 상태 설정
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // 에러 설정
      setError: (error: string | null) => {
        set({ error });
      },

      // 에러 클리어
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);
